import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IPromptsLibraryContract } from "../model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { IRouterContract } from "@features/shared/router";
import { IPrompt } from "@features/shared/entities/prompts-library";
import { ICurrentTipContract } from "@features/tips/ui/current";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IPersonalBoostPackContract } from "@features/upsell/ui/personal-boost-pack";
import { IUltimateAiPackContract } from "@features/upsell/ui/ultimate-ai-pack";
import { ICompleteAiPackContract } from "@features/upsell/ui/complete-ai-pack";
import { IClaudeWorkBundleContract } from "@features/upsell/ui/claude-work-bundle";
import { reset } from "patronum";
import { IUpsellContract } from "@features/upsell/model";

const categoryClicked = createEvent<{ category: string; categoryId: number; type: "top" | "sub" }>();
const subcategoryClicked = createEvent<{ category: string; categoryId: number; type: "top" | "sub" }>();
const tryButtonClicked = createEvent<IPrompt>();
const copyButtonClicked = createEvent<IPrompt>();
const unlockButtonClicked = createEvent<{
    placement: "button" | "prompt" | "banner";
    promptId?: number;
    promptTitle?: string;
    promptText?: string;
    category?: string;
    categoryId?: number;
}>();
const packClicked = createEvent<{ placement: "home" | "profile" | "rail"; pack: string }>();

const $categoryTitle = createStore<string>("");
const $categoryId = createStore<number | null>(null);
const $subcategoryTittle = createStore<string>("");
const $promptsTitle = createStore<string>("");
const $packBought = createStore<boolean | null>(null);
const $packType = createStore<"personal_boost" | "ultimate_ai" | null>(null);
const $isStatusLoaded = createStore<boolean>(true);

const setAnchorFx = createEffect((promptId: number) => {
    if (typeof window === "undefined") return;
    const currentUrl = window.location.pathname + window.location.search;
    const urlWithHash = `${currentUrl}#prompt-${promptId}`;
    window.history.replaceState(null, "", urlWithHash);
});

const setPrevUrlInStorageFx = createEffect((url: string) => {
    if (typeof window === "undefined") return;
    localStorage.setItem("prevUrl", url);
});

const contract = new ModelContract(
    { $categoryTitle, $subcategoryTittle, $promptsTitle, $packBought, $packType, $categoryId, $isStatusLoaded },
    { categoryClicked, subcategoryClicked, tryButtonClicked, copyButtonClicked, unlockButtonClicked, packClicked },
    {},
    {}
);

export type IPromptsLibraryUIContract = typeof contract;

export class PromptsLibraryUIModule extends ModuleDescription<IPromptsLibraryUIContract> {
    constructor(
        public promptsLibrary: IPromptsLibraryContract,
        public router: IRouterContract,
        public routes: IAppRouteUnitsContract,
        public tips: ICurrentTipContract,
        public personalBoostPackModule: IPersonalBoostPackContract,
        public ultimateAiPackModule: IUltimateAiPackContract,
        public completeAiPackModule: ICompleteAiPackContract,
        public claudeWorkBundleModule: IClaudeWorkBundleContract,
        public upsellModule: IUpsellContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: packClicked,
            filter: (s) => s.pack === "claude_pack",
            fn: () => `/prompts-library/claude_pack`,
            target: this.router.events.routerPushWithoutLoader
        });

        sample({
            clock: packClicked,
            filter: (s) => s.pack !== "claude_pack",
            fn: (s) => `/prompts-library?pack=${s.pack}`,
            target: this.router.events.routerPushWithoutLoader
        });

        sample({
            clock: categoryClicked,
            source: {
                categories: this.promptsLibrary.stores.$allCategories,
                query: this.routes.stores.promptsLibrary.$query
            },
            filter: (s, _) => s.categories.length > 0,
            fn: (s, p) => `/prompts-library/category/${p.categoryId}?pack=${s.query.pack}`,
            target: this.router.events.routerPushWithoutLoader
        });

        sample({
            clock: subcategoryClicked,
            source: {
                subcategories: this.promptsLibrary.stores.$subcategories,
                query: this.routes.stores.promptsLibrary.$query
            },
            filter: (s, _) => s.subcategories.length > 0,
            fn: (s, p) => {
                const subcategory = s.subcategories.find((s) => s.id === p.categoryId);
                return `/prompts-library/category/${subcategory!.parent_id}/subcategory/${subcategory!.id}?pack=${s.query.pack}`;
            },
            target: this.router.events.routerPushWithoutLoader
        });

        sample({
            clock: [categoryClicked, subcategoryClicked],
            fn: (s) => s.categoryId,
            target: $categoryId
        });

        sample({
            clock: tryButtonClicked,
            fn: (s) => s.id,
            target: setAnchorFx
        });

        sample({
            clock: setAnchorFx.done,
            fn: () => `/assistant`,
            target: this.router.events.routerPush
        });

        sample({
            clock: tryButtonClicked,
            fn: ({ text }) => text,
            target: this.tips.stores.$currentPrompt
        });

        sample({
            source: this.routes.stores.promptsLibrary.$query,
            filter: (s) => Boolean(s.pack),
            fn: (s) => {
                if (s.pack === "ultimate_ai") {
                    return "The ultimate AI power pack";
                } else if (s.pack === "personal_boost") {
                    return "Personal Boost Pack";
                }
                return "";
            },
            target: $categoryTitle
        });

        sample({
            source: {
                categories: this.promptsLibrary.stores.$allCategories,
                params: this.routes.stores.promptsLibrary.$params
            },
            filter: (s) => s.categories.length > 0 && Boolean(s.params.categoryId),
            fn: (s) => {
                const category = s.categories.find((c) => c.id === Number(s.params.categoryId));
                return category?.title || "";
            },
            target: $subcategoryTittle
        });

        sample({
            source: {
                subcategories: this.promptsLibrary.stores.$subcategories,
                params: this.routes.stores.promptsLibrary.$params
            },
            filter: (s) => s.subcategories.length > 0 && Boolean(s.params.subcategoryId),
            fn: (s) => {
                const subcategory = s.subcategories.find((c) => c.id === Number(s.params.subcategoryId));

                return subcategory?.title || "";
            },
            target: $promptsTitle
        });

        sample({
            source: this.routes.stores.promptsLibrary.$query,
            filter: (s) => Boolean(s.pack),
            fn: (s) => {
                if (s.pack === "ultimate_ai") {
                    return "ultimate_ai";
                } else if (s.pack === "personal_boost") {
                    return "personal_boost";
                }

                return null;
            },
            target: $packType
        });

        sample({
            source: {
                query: this.routes.stores.promptsLibrary.$query,
                personalBoostPackStatus: this.personalBoostPackModule.stores.$personalBoostPackStatus,
                ultimateAiPackStatus: this.ultimateAiPackModule.stores.$ultimateAiPackStatus,
                completeAiPackStatus: this.completeAiPackModule.stores.$completeAiPackStatus,
                claudeWorkBundleStatus: this.claudeWorkBundleModule.stores.$claudeWorkBundleStatus
            },
            fn: (s) => {
                if (s.query.pack === "personal_boost") {
                    const status = s.personalBoostPackStatus;
                    return status === "Success" || status === "Processing";
                }

                if (s.query.pack === "ultimate_ai") {
                    const ultimateStatus = s.ultimateAiPackStatus;
                    const completeStatus = s.completeAiPackStatus;
                    const claudeStatus = s.claudeWorkBundleStatus;

                    return (
                        ultimateStatus === "Success" ||
                        ultimateStatus === "Processing" ||
                        completeStatus === "Success" ||
                        completeStatus === "Processing" ||
                        claudeStatus === "Success" ||
                        claudeStatus === "Processing"
                    );
                }

                return false;
            },
            target: $packBought
        });

        sample({
            source: {
                packType: $packType,
                personalBoostPackStatus: this.personalBoostPackModule.stores.$personalBoostPackStatus,
                ultimateAiPackStatus: this.ultimateAiPackModule.stores.$ultimateAiPackStatus,
                completeAiPackStatus: this.completeAiPackModule.stores.$completeAiPackStatus,
                claudeWorkBundleStatus: this.claudeWorkBundleModule.stores.$claudeWorkBundleStatus,
                ultimateUpsellIds: this.ultimateAiPackModule.stores.$upsellIds,
                completeUpsellIds: this.completeAiPackModule.stores.$upsellIds,
                claudeUpsellIds: this.claudeWorkBundleModule.stores.$upsellIds
            },
            fn: (s) => {
                if (s.packType === "personal_boost") {
                    return s.personalBoostPackStatus !== null;
                }
                if (s.packType === "ultimate_ai") {
                    const ultimatePending = s.ultimateUpsellIds.length > 0 && s.ultimateAiPackStatus === null;
                    const completePending = s.completeUpsellIds.length > 0 && s.completeAiPackStatus === null;
                    const claudePending = s.claudeUpsellIds.length > 0 && s.claudeWorkBundleStatus === null;
                    return !ultimatePending && !completePending && !claudePending;
                }
                return true;
            },
            target: $isStatusLoaded
        });

        sample({
            clock: unlockButtonClicked,
            source: {
                query: this.routes.stores.promptsLibrary.$query,
                ultimateAiPackStatus: this.ultimateAiPackModule.stores.$ultimateAiPackStatus,
                completeAiPackStatus: this.completeAiPackModule.stores.$completeAiPackStatus
            },
            filter: (s) => Boolean(s.query.pack),
            fn: (s) => {
                if (s.query.pack === "personal_boost") {
                    return `/upsell/600_prompts`;
                }
                if (s.query.pack === "ultimate_ai") {
                    if (s.completeAiPackStatus !== null) {
                        return `/upsell/30k_prompts_complete_pack`;
                    }
                    if (s.ultimateAiPackStatus !== null) {
                        return `/upsell/30k_prompts`;
                    }

                    return `/upsell/30k_prompts_complete_pack`;
                }
                return "";
            },
            target: this.router.events.routerReplace
        });

        sample({
            clock: unlockButtonClicked,
            source: { path: this.routes.stores.promptsLibrary.$path, query: this.routes.stores.promptsLibrary.$query },
            fn: (s) => `${s.path}?pack=${s.query.pack}`,
            target: setPrevUrlInStorageFx
        });

        reset({
            clock: this.routes.stores.promptsLibrary.closed,
            target: [$packBought, $isStatusLoaded]
        });
    }
}
